import { StatutColis } from '../types';

/**
 * Module E-Commerce & Suivi de Livraison (spéc. « E commerce.docx ») —
 * constantes propres à cette typologie, isolées des autres modules.
 */

export const ECOMMERCE_TENANT_TYPES = ['ecommerce', 'ecommerce_structure'];

export const isEcommerceType = (type?: string | null): boolean =>
  !!type && ECOMMERCE_TENANT_TYPES.includes(type);

/** Incidents de livraison saisis par le livreur (UNREACHABLE, RESCHEDULED, ...). */
export const INCIDENT_STATUTS: StatutColis[] = [
  StatutColis.INJOIGNABLE,
  StatutColis.ABSENT,
  StatutColis.ADRESSE_INTROUVABLE,
  StatutColis.REPORTE,
  StatutColis.REFUSE,
  StatutColis.PAIEMENT_REFUSE,
];

/** Statuts réservés aux tenants e-commerce (refusés par l'API pour les autres). */
export const ECOMMERCE_ONLY_STATUTS: StatutColis[] = [StatutColis.EN_PREPARATION];

/**
 * Typologies qui livrent chez le client et gèrent donc les incidents de
 * livraison : e-commerce et transport local (livraison urbaine).
 */
export const INCIDENT_CAPABLE_TYPES = [...ECOMMERCE_TENANT_TYPES, 'transport_local'];

export const supportsIncidents = (type?: string | null): boolean =>
  !!type && INCIDENT_CAPABLE_TYPES.includes(type);

/** Statuts « terrain » (livreur sur place + incidents) des typologies à incidents. */
export const INCIDENT_CAPABLE_STATUTS: StatutColis[] = [StatutColis.LIVREUR_SUR_PLACE, ...INCIDENT_STATUTS];

/** Statuts qu'un tenant de ce type n'a PAS le droit d'utiliser. */
export const forbiddenStatuts = (type?: string | null): StatutColis[] => [
  ...(isEcommerceType(type) ? [] : ECOMMERCE_ONLY_STATUTS),
  ...(supportsIncidents(type) ? [] : INCIDENT_CAPABLE_STATUTS),
];

/** Progression « terrain » d'un livreur e-commerce (hors incidents). */
export const ECOMMERCE_PROGRESS_STATUTS: StatutColis[] = [
  StatutColis.EN_LIVRAISON,
  StatutColis.LIVREUR_SUR_PLACE,
];

export const ECOMMERCE_STATUT_LABELS: Record<string, string> = {
  en_preparation: "En cours d'emballage",
  en_livraison: 'Départ en livraison',
  livreur_sur_place: 'Je suis sur place',
  injoignable: 'Client injoignable',
  absent: 'Client absent',
  adresse_introuvable: 'Adresse / repère introuvable',
  reporte: 'Reporter la livraison',
  refuse: 'Produit refusé',
  paiement_refuse: 'Paiement refusé',
  retour_en_cours: 'Retour vers le vendeur',
};

/**
 * Créneau de livraison souhaité par le destinataire — self-service depuis la
 * page de suivi publique (spéc. 25/09/26), pour que le livreur planifie sa
 * tournée. Doit rester synchronisé avec CRENEAUX_LIVRAISON côté backend.
 */
export type CreneauLivraison = 'matin' | 'apres_midi' | 'soir';

export const CRENEAUX_LIVRAISON: { value: CreneauLivraison; label: string }[] = [
  { value: 'matin', label: 'Matin (8h-12h)' },
  { value: 'apres_midi', label: 'Après-midi (12h-16h)' },
  { value: 'soir', label: 'Soir (16h-19h)' },
];

export const CRENEAU_LIVRAISON_LABEL: Record<CreneauLivraison, string> = Object.fromEntries(
  CRENEAUX_LIVRAISON.map((c) => [c.value, c.label]),
) as Record<CreneauLivraison, string>;

export type PaymentMethod = 'cash' | 'momo_courier' | 'om_courier' | 'momo_merchant' | 'om_merchant';

export const PAYMENT_METHODS: { value: PaymentMethod; label: string; hint: string }[] = [
  { value: 'cash', label: 'Espèces', hint: 'Le livreur détient l\'argent' },
  { value: 'momo_courier', label: 'MTN MoMo → livreur', hint: 'Payé sur le numéro du livreur' },
  { value: 'om_courier', label: 'Orange Money → livreur', hint: 'Payé sur le numéro du livreur' },
  { value: 'momo_merchant', label: 'MTN MoMo → vendeur', hint: 'Payé directement au vendeur' },
  { value: 'om_merchant', label: 'Orange Money → vendeur', hint: 'Payé directement au vendeur' },
];

export const PAYMENT_METHOD_LABEL: Record<string, string> = Object.fromEntries(
  PAYMENT_METHODS.map((m) => [m.value, m.label]),
);

/** Couleurs des statuts e-commerce sur fond sombre (apps livreur / portail). */
export const ECOMMERCE_STATUT_DARK_COLORS: Record<string, string> = {
  en_preparation: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  livreur_sur_place: 'bg-lime-500/20 text-lime-300 border-lime-500/30',
  injoignable: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  absent: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  adresse_introuvable: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  reporte: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  refuse: 'bg-red-500/20 text-red-300 border-red-500/30',
  paiement_refuse: 'bg-red-500/20 text-red-300 border-red-500/30',
};
